/**
 * Global Assistant Panel
 *
 * A right sidebar panel that provides a unified AI assistant interface.
 * Accessible from anywhere in the application via TopNavigation.
 * Features: resizable width, image upload support.
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, Trash2, StopCircle, Send, Sparkles, ChevronDown, ImagePlus, GripVertical, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import { AssistantMessage } from './AssistantMessage';
import { ConversationSelector } from './ConversationSelector';

// Panel width constraints
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 600;

// Suggested prompts based on context type
const SUGGESTED_PROMPTS: Record<string, string[]> = {
  editor: [
    'Help me create a new agent',
    'Explain this agent configuration',
    'What MCP servers are available?',
    'Fix the validation errors',
  ],
  playground: [
    'Why did my agent fail?',
    'How can I fix this error?',
    'Explain what this agent does',
    'Optimize this agent',
  ],
  evals: [
    'Show me available evals',
    'Run the latest evaluation',
    'Compare the variants',
    'Why did the trials fail?',
  ],
  schemas: [
    'Explain this schema',
    'What fields are required?',
    'Validate the schema',
  ],
  general: [
    'What can you help me with?',
    'List available tools',
    'Show me available agents',
  ],
};

interface ImageAttachment {
  id: string;
  file: File;
  preview: string;
}

export function GlobalAssistantPanel() {
  const {
    isOpen,
    closePanel,
    pageContext,
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    stopGeneration,
    model,
    setModel,
    availableModels,
    modelsLoading,
    hasAnyKeys,
    panelWidth,
    setPanelWidth,
  } = useGlobalAssistant();

  const [input, setInput] = useState('');
  const [images, setImages] = useState<ImageAttachment[]>([]);
  const [isResizing, setIsResizing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      setPanelWidth(Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && images.length === 0) || isLoading) return;

    // Build message with images if any
    let messageContent = input.trim();
    if (images.length > 0) {
      // For now, we'll include images as base64 in the message
      // The backend will need to handle multimodal content
      const imageDescriptions = images.map((img) => `[Image: ${img.file.name}]`).join(' ');
      messageContent = messageContent ? `${messageContent}\n\n${imageDescriptions}` : imageDescriptions;
    }

    setInput('');
    setImages([]);

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    // Pass images to sendMessage if backend supports it
    await sendMessage(messageContent, images.map(img => img.file));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: ImageAttachment[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        newImages.push({
          id: `${Date.now()}-${i}`,
          file,
          preview: URL.createObjectURL(file),
        });
      }
    }
    setImages((prev) => [...prev, ...newImages]);
    e.target.value = ''; // Reset input
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter((i) => i.id !== id);
    });
  };

  const suggestedPrompts = SUGGESTED_PROMPTS[pageContext.contextType] || SUGGESTED_PROMPTS.general;

  if (!isOpen) return null;

  return (
    <>
      {/* Resize overlay */}
      {isResizing && (
        <div className="fixed inset-0 z-50 cursor-ew-resize" />
      )}

      <div
        className="fixed right-0 top-14 bottom-0 border-l bg-background z-50 flex flex-col shadow-lg"
        style={{ width: panelWidth }}
      >
        {/* Resize handle */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-primary/20 transition-colors group flex items-center justify-center"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute left-0 w-4 h-full" /> {/* Larger hit area */}
          <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground absolute -left-1.5" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-500" />
            <span className="font-medium text-sm">Assistant</span>
            <span className="text-xs text-muted-foreground capitalize">
              ({pageContext.contextType})
            </span>
          </div>
          <div className="flex items-center gap-1">
            {/* Model selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" disabled={modelsLoading}>
                  {modelsLoading
                    ? 'Loading...'
                    : availableModels.length === 0
                      ? 'No models'
                      : availableModels.find((m) => m.id === model)?.name || 'Select model'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {availableModels.length === 0 ? (
                  <DropdownMenuItem disabled className="text-muted-foreground">
                    Configure API keys in Settings
                  </DropdownMenuItem>
                ) : (
                  availableModels.map((m) => (
                    <DropdownMenuItem
                      key={m.id}
                      onClick={() => setModel(m.id)}
                      className={cn(m.id === model && 'bg-accent')}
                    >
                      {m.name}
                      {m.default && <span className="ml-2 text-xs text-muted-foreground">(default)</span>}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Conversation selector */}
            <ConversationSelector />

            {/* Clear button */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={clearMessages}
              title="New conversation"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>

            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={closePanel}
              title="Close assistant"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* No API Keys Warning */}
        {hasAnyKeys === false && (
          <Alert variant="default" className="m-3 border-yellow-300 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
            <AlertTitle className="text-yellow-800 dark:text-yellow-300">No API Keys Configured</AlertTitle>
            <AlertDescription className="text-yellow-700 dark:text-yellow-400">
              Add an API key in{' '}
              <Link to="/settings" className="underline font-medium" onClick={closePanel}>
                Settings → API Keys
              </Link>{' '}
              to use the assistant.
            </AlertDescription>
          </Alert>
        )}

        {/* Messages */}
        <ScrollArea className="flex-1 p-4 [&_[data-radix-scroll-area-viewport]>div]:!block">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <div className="text-center text-muted-foreground text-sm">
                <Sparkles className="h-8 w-8 mx-auto mb-2 text-purple-500/50" />
                <p>Hi! I'm your AI assistant.</p>
                <p className="text-xs mt-1">
                  I can help you with agents, skills, evaluations, and more.
                </p>
              </div>

              {/* Suggested prompts */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Suggested:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedPrompts.map((prompt, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => handleSuggestedPrompt(prompt)}
                    >
                      {prompt}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 w-full max-w-full overflow-hidden">
              {messages.map((message) => (
                <AssistantMessage key={message.id} message={message} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Error display */}
        {error && (
          <div className="px-4 py-2 bg-destructive/10 border-t border-destructive/20">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        {/* Image previews */}
        {images.length > 0 && (
          <div className="px-3 py-2 border-t bg-muted/20">
            <div className="flex gap-2 flex-wrap">
              {images.map((img) => (
                <div key={img.id} className="relative group">
                  <img
                    src={img.preview}
                    alt={img.file.name}
                    className="h-12 w-12 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    className="absolute -top-1 -right-1 h-4 w-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-3 border-t bg-muted/30">
          <div className="flex gap-2 items-end">
            {/* Image upload button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => fileInputRef.current?.click()}
              title="Attach image"
              disabled={isLoading}
            >
              <ImagePlus className="h-4 w-4" />
            </Button>

            {/* Text input */}
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything..."
              rows={1}
              className="flex-1 min-h-9 max-h-32 px-3 py-2 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              disabled={isLoading}
              style={{ height: 'auto', overflow: 'hidden' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${Math.min(target.scrollHeight, 128)}px`;
              }}
            />

            {/* Send/Stop button */}
            {isLoading ? (
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="h-9 w-9 shrink-0"
                onClick={stopGeneration}
                title="Stop generating"
              >
                <StopCircle className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                className="h-9 w-9 shrink-0"
                disabled={!model || modelsLoading || (!input.trim() && images.length === 0)}
                title={!model ? "Configure API keys to send messages" : "Send message"}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        </form>
      </div>
    </>
  );
}

export default GlobalAssistantPanel;
